import { useEffect, useState } from 'react';
import Button from '../../common/button/Button';
import { useWfTyphoonStore } from 'src/api/wf/stores/WfTyphoonStore';
import useWfTyphoonService from 'src/api/wf/service/WfDisasterService';
import { useWfFilterStore } from 'src/api/wf/stores/WfFilterStore';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  localDateStringToInstant,
  unixToLocalDateString,
} from 'src/common/dateStringtoUnix/dateStringtoUnix';
import useWfDisasterRepo from 'src/api/wf/repository/WfDisasterRepo';
import { useWfUserStore } from 'src/api/wf/stores/WfUserStore';
import { useAlertStore } from 'src/ui/common/alert/AlertStore';
import Alert from 'src/ui/common/alert/Alert';
import useWfWorkplaceService from 'src/api/wf/service/WfWorkplaceService';
import { isPastDate } from 'src/common/isPastDate/isPastDate';
import { useNotify } from 'src/ui/common/alert/useNotify';

function WF_4_11_1_SystemLaunch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { postDisasterSystemLaunchRequest } = useWfDisasterRepo();
  const { currentTyphoon } = useWfTyphoonStore();
  const { user } = useWfUserStore();
  const { fetchLatestTyphoon, registerTyphoon } = useWfTyphoonService();
  const { fetchBranchOptions } = useWfWorkplaceService();
  const [isTyphoonActive, setIsTyphoonActive] = useState<boolean>(false);
  const { branchOptions } = useWfFilterStore();
  const { showAlert, hideAlert } = useAlertStore();
  const [branches, setBranches] = useState<string[]>([]);
  const [typhoonName, setTyphoonName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [stopDate, setStopDate] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const notify = useNotify();

  useEffect(() => {
    if (!currentTyphoon) return;

    setTyphoonName(currentTyphoon.name);
    setStartDate(unixToLocalDateString(currentTyphoon.startDateTime));
    setStopDate(unixToLocalDateString(currentTyphoon.stopDateTime));
    setBranches(currentTyphoon.targetBranchIds);

    if (currentTyphoon.launchDateTime && !currentTyphoon.shutdownDateTime) setIsTyphoonActive(true);
    if (currentTyphoon.shutdownDateTime) setIsTyphoonActive(false);
  }, [currentTyphoon]);

  useEffect(() => {
    if (branches.length !== 0) return;

    fetchBranchOptions();
  }, [branches, fetchBranchOptions]);

  const clearForm = () => {
    setTyphoonName('');
    setStartDate('');
    setStopDate('');
  };

  const validateTyphoonRegistration = () => {
    if (!branches.length) {
      notify('wf.systemLaunch.alert.noBranches');
      return false;
    }

    if (!typhoonName) {
      notify('wf.systemLaunch.alert.noTyphoonName');
      return false;
    }

    if (!startDate) {
      notify('wf.systemLaunch.alert.noStartDateTime');
      return false;
    }

    if (!stopDate) {
      notify('wf.systemLaunch.alert.noEndDateTime');
      return false;
    }

    const stopTs = new Date(stopDate).getTime();
    if (!isNaN(stopTs) && stopTs < Date.now()) {
      notify('wf.systemLaunch.alert.pastEndDateTime');
      return false;
    }

    const startTs = new Date(startDate).getTime();
    if (!isNaN(startTs) && !isNaN(stopTs) && startTs >= stopTs) {
      notify('wf.systemLaunch.alert.startDateAfterEndDate');
      return false;
    }

    return true;
  };

  const isUnLaunchedTyphhoon = !currentTyphoon?.launchDateTime && !currentTyphoon?.shutdownDateTime;

  const handleSystemLaunch = async () => {
    if (!user) return;

    if (!validateTyphoonRegistration()) return;

    // Validate start datetime: must be in future
    const startTs = new Date(startDate).getTime();
    const currentTs = Date.now();
    if (!isNaN(startTs) && startTs <= currentTs) {
      showAlert({
        content: (
          <Alert message={t('wf.systemLaunch.alert.startDateTimeInvalid')} onClose={hideAlert} />
        ),
      });
      return;
    }

    setIsLoading(true);
    try {
      /**
       * If Control Center API has failed, there is one disaster record.
       * Disaster record should be created only when:
       *   (1) There is no disaster record in typhoon.disaster table
       *   (2) There is no unlaunched disaster record in typhoon.disaster table
       * If there is an unlaunched disaster record in typhoon.disaster table, it should be updated instead of creating a new one.
       */
      let registrationResponse;
      if (currentTyphoon == null || !isUnLaunchedTyphhoon) {
        registrationResponse = await registerTyphoon(
          typhoonName,
          branches,
          localDateStringToInstant(startDate),
          localDateStringToInstant(stopDate)
        );
      } else {
        registrationResponse = await registerTyphoon(
          typhoonName,
          branches,
          localDateStringToInstant(startDate),
          localDateStringToInstant(stopDate),
          currentTyphoon.id
        );
      }

      if (!registrationResponse?.id) {
        notify('wf.systemLaunch.alert.registrationFailed');
        return;
      }

      const launchResponse = await postDisasterSystemLaunchRequest({
        disasterId: registrationResponse.id,
        userId: user.id,
        launchDateTime: localDateStringToInstant(new Date()),
      });

      if (launchResponse) {
        notify('wf.systemLaunch.alert.systemLaunchSuccessful');
      } else {
        notify('wf.systemLaunch.alert.systemLaunchFailed');
      }
    } catch (error) {
      console.error(error);
      notify('wf.systemLaunch.alert.systemLaunchFailed');
    } finally {
      setIsLoading(false);
      clearForm();
      fetchLatestTyphoon();
    }
  };

  const handleSystemShutdown = () => {
    if (!currentTyphoon || !user) return;

    if (isPastDate(currentTyphoon.stopDateTime)) {
      handleSystemShutdownRequest(currentTyphoon.id, user.id);
    } else {
      showAlert({
        content: (
          <Alert
            message={t('wf.systemLaunch.alert.systemRunning')}
            onClose={() => handleSystemShutdownRequest(currentTyphoon.id, user.id)}
            backButton
            backButtonAction={hideAlert}
          />
        ),
      });
    }
  };

  const handleSystemShutdownRequest = async (disasterId: string, userId: string) => {
    setIsLoading(true);
    try {
      const response = await postDisasterSystemLaunchRequest({
        disasterId,
        userId,
        shutdownDateTime: localDateStringToInstant(new Date()),
      });

      if (response) {
        notify('wf.systemLaunch.alert.systemShutdownSuccessful');
      } else {
        notify('wf.systemLaunch.alert.systemShutdownFailed');
      }
    } catch (error) {
      console.error(error);
      notify('wf.systemLaunch.alert.systemShutdownFailed');
    } finally {
      setIsLoading(false);
      fetchLatestTyphoon();
    }
  };

  const validateStartDateTimeForUpdate = (): boolean => {
    if (!currentTyphoon?.launchDateTime) return true;

    const startTs = new Date(startDate).getTime();
    const launchTs = Number(currentTyphoon.launchDateTime) * 1000;
    const currentStartTs = Number(currentTyphoon.startDateTime) * 1000;

    if (startTs != currentStartTs && currentStartTs < Date.now()) {
      showAlert({
        content: (
          <Alert
            message={t('wf.systemLaunch.alert.cannotChangeStartDateTime')}
            onClose={hideAlert}
          />
        ),
      });
      return false;
    }

    if (!isNaN(startTs) && !isNaN(launchTs) && startTs <= launchTs) {
      showAlert({
        content: (
          <Alert message={t('wf.systemLaunch.alert.startDateTimeInvalid')} onClose={hideAlert} />
        ),
      });
      return false;
    }

    return true;
  };

  const handleTyphoonUpdate = async () => {
    if (!currentTyphoon) return;

    if (!validateTyphoonRegistration()) return;
    if (!validateStartDateTimeForUpdate()) return;

    setIsLoading(true);
    try {
      const response = await registerTyphoon(
        typhoonName,
        branches,
        localDateStringToInstant(startDate),
        localDateStringToInstant(stopDate),
        currentTyphoon.id
      );

      if (response) {
        notify('wf.systemLaunch.alert.updateSuccessful');
      } else {
        notify('wf.systemLaunch.alert.updateFailed');
      }
    } catch (error) {
      console.error(error);
      notify('wf.systemLaunch.alert.updateFailed');
    } finally {
      setIsLoading(false);
      fetchLatestTyphoon();
    }
  };

  const toggleBranch = (branch: string) => {
    setBranches((prev) => {
      const isTargetBranch = isTyphoonActive && currentTyphoon?.targetBranchIds.includes(branch);

      if (isTargetBranch) {
        return prev;
      }

      const isSelected = prev.includes(branch);

      if (isSelected) {
        return prev.filter((b) => b !== branch);
      } else {
        return [...prev, branch];
      }
    });
  };

  const handleBackButton = () => {
    navigate(-1);
  };

  const buttonDisableLogic = {
    systemLaunch: isTyphoonActive || isLoading,

    updateTyphoon:
      !isTyphoonActive || !currentTyphoon || Boolean(currentTyphoon.shutdownDateTime) || isLoading,

    systemShutdown: !isTyphoonActive || isLoading,
  };

  return (
    <div className={`mx-5 mt-1 justify-center`}>
      <div className="mb-4 flex justify-between">
        <div className="grid w-full grid-cols-4 gap-2">
          <div className="font-bold">{t('wf.common.status')}</div>
          <div>
            {isTyphoonActive ? t('wf.systemLaunch.launched') : t('wf.systemLaunch.finished')}
          </div>
        </div>
      </div>
      <div className="mb-4">
        <div className="mb-2 font-bold">{t('wf.systemLaunch.targetBranchesForImport')}</div>
        <div className="max-h-64 overflow-y-auto rounded border p-3">
          <div className="grid grid-cols-4 gap-2">
            {branchOptions?.map((branch) => (
              <label key={branch.code} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={branches.includes(branch.code)}
                  onChange={() => toggleBranch(branch.code)}
                />
                {branch.name}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="mb-4">
        <label className="mb-1 block font-bold">{t('wf.systemLaunch.typhoonName')}</label>
        <input
          type="text"
          className="w-full rounded border px-2 py-1"
          value={typhoonName}
          onChange={(e) => setTyphoonName(e.target.value)}
        />
      </div>
      <div className="mb-6 flex items-center gap-2">
        <div className="flex flex-col">
          <label className="mb-1">{t('wf.systemLaunch.start')}</label>
          <input
            type="datetime-local"
            step={3600}
            className="rounded border px-2 py-1"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <span className="mt-6">~</span>
        <div className="flex flex-col">
          <label className="mb-1">{t('wf.systemLaunch.end')}</label>
          <input
            type="datetime-local"
            step={3600}
            className="rounded border px-2 py-1"
            value={stopDate}
            onChange={(e) => setStopDate(e.target.value)}
          />
        </div>
      </div>
      <div className="my-2 flex justify-center gap-6">
        {!isTyphoonActive ? (
          <Button
            title={t('wf.systemLaunch.systemLaunch')}
            onClick={handleSystemLaunch}
            disabled={buttonDisableLogic.systemLaunch}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <Button
              title={t('wf.systemLaunch.update')}
              onClick={handleTyphoonUpdate}
              disabled={buttonDisableLogic.updateTyphoon}
            />
            <Button
              title={t('wf.systemLaunch.systemShutdown')}
              onClick={handleSystemShutdown}
              disabled={buttonDisableLogic.systemShutdown}
            />
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <Button title={t('wf.common.back')} onClick={handleBackButton} />
      </div>
    </div>
  );
}

export default WF_4_11_1_SystemLaunch;
